import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  permission: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const { permissions, user } = useAuth();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useApp();

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const navSections: NavSection[] = [
    {
      label: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', permission: permissions?.dashboard ?? false },
      ],
    },
    {
      label: 'Reception',
      items: [
        { path: '/reception/shifts', label: 'Shifts', icon: 'ri-time-line', permission: permissions?.reception.shifts ?? false },
        { path: '/reception/checkin', label: 'Check-in', icon: 'ri-login-box-line', permission: permissions?.reception.checkin ?? false },
        { path: '/reception/checkout', label: 'Check-out', icon: 'ri-logout-box-line', permission: permissions?.reception.checkout ?? false },
        { path: '/reception/billing', label: 'Billing & Receipts', icon: 'ri-bill-line', permission: permissions?.reception.billing ?? false },
        { path: '/reception/cash', label: 'Cash Tracking', icon: 'ri-cash-line', permission: permissions?.reception.cash ?? false },
        { path: '/reception/night-audit', label: 'Night Audit', icon: 'ri-moon-line', permission: permissions?.reception.logs ?? false },
        { path: '/reception/logs', label: 'Activity Logs', icon: 'ri-file-list-line', permission: permissions?.reception.logs ?? false },
        { path: '/reception/reports', label: 'Reports', icon: 'ri-bar-chart-line', permission: permissions?.reception.reports ?? false },
      ],
    },
    {
      label: 'Bookings',
      items: [
        { path: '/bookings/rooms', label: 'Rooms', icon: 'ri-hotel-bed-line', permission: permissions?.bookings.rooms ?? false },
        { path: '/bookings/new', label: 'New Booking', icon: 'ri-add-circle-line', permission: permissions?.bookings.new ?? false },
        { path: '/bookings/manage', label: 'Manage Bookings', icon: 'ri-calendar-check-line', permission: permissions?.bookings.manage ?? false },
        { path: '/bookings/guests', label: 'Guest Directory', icon: 'ri-user-search-line', permission: permissions?.bookings.guests ?? false },
        { path: '/bookings/billing', label: 'GST Billing', icon: 'ri-money-rupee-circle-line', permission: permissions?.bookings.billing ?? false },
        { path: '/bookings/rate-calendar', label: 'Rate Calendar', icon: 'ri-calendar-event-line', permission: permissions?.bookings.billing ?? false },
        { path: '/bookings/reports', label: 'Reports', icon: 'ri-pie-chart-line', permission: permissions?.bookings.reports ?? false },
      ],
    },
    {
      label: 'Inventory',
      items: [
        { path: '/inventory/stock', label: 'Stock Levels', icon: 'ri-box-3-line', permission: permissions?.inventory.stock ?? false },
        { path: '/inventory/purchases', label: 'Purchases', icon: 'ri-shopping-cart-line', permission: permissions?.inventory.purchases ?? false },
        { path: '/inventory/vendors', label: 'Vendors', icon: 'ri-building-line', permission: permissions?.inventory.vendors ?? false },
        { path: '/inventory/reports', label: 'Reports', icon: 'ri-archive-line', permission: permissions?.inventory.reports ?? false },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/housekeeping', label: 'Housekeeping', icon: 'ri-brush-line', permission: permissions?.canteen.menu ?? false },
        { path: '/expenses/daily', label: 'Daily Expenses', icon: 'ri-money-dollar-circle-line', permission: permissions?.canteen.menu ?? false },
      ],
    },
    {
      label: 'Canteen',
      items: [
        { path: '/canteen/menu', label: 'Menu', icon: 'ri-restaurant-line', permission: permissions?.canteen.menu ?? false },
        { path: '/canteen/billing', label: 'Billing & KOT', icon: 'ri-bill-line', permission: permissions?.canteen.billing ?? false },
        { path: '/canteen/history', label: 'Bill History', icon: 'ri-history-line', permission: permissions?.canteen.history ?? false },
        { path: '/canteen/reports', label: 'Reports', icon: 'ri-pie-chart-line', permission: permissions?.canteen.reports ?? false },
      ],
    },
    {
      label: 'Admin',
      items: [
        { path: '/users/manage', label: 'User Management', icon: 'ri-shield-user-line', permission: permissions?.manageUsers ?? false },
        { path: '/users/permissions', label: 'Permissions', icon: 'ri-key-2-line', permission: permissions?.manageUsers ?? false },
      ],
    },
  ];

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen bg-navy-900 text-white transition-all duration-300 flex flex-col ${
        sidebarOpen ? 'w-64 translate-x-0' : 'w-0 -translate-x-full lg:w-20 lg:translate-x-0'
      }`}
    >
      {/* Logo area */}
      <div className="flex items-center gap-3 px-5 h-16 border-b border-navy-700 flex-shrink-0">
        <div className="w-9 h-9 rounded-lg bg-gold-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-hotel-line text-navy-900 text-lg" />
        </div>
        {sidebarOpen && (
          <div className="font-display text-xl font-semibold tracking-wide text-white">
            Hotel<span className="text-gold-400">Pro</span>
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className={`ml-auto text-navy-300 hover:text-white lg:hidden ${!sidebarOpen ? 'hidden' : ''}`}
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {navSections.map((section) => {
          const visibleItems = section.items.filter((item) => item.permission);
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.label} className="mb-4">
              {sidebarOpen && (
                <div className="px-3 mb-2 text-[11px] font-semibold text-navy-400 uppercase tracking-wider">
                  {section.label}
                </div>
              )}
              {visibleItems.map((item) => {
                const isActive = pathname === item.path || pathname.startsWith(`${item.path}/`);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-gold-500/15 text-gold-400 border border-gold-500/20'
                        : 'text-navy-200 hover:bg-navy-800 hover:text-white'
                    } ${!sidebarOpen ? 'justify-center' : ''}`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="flex-shrink-0 border-t border-navy-700 p-3">
        {user && sidebarOpen && (
          <div className="px-3 py-2 rounded-lg bg-navy-800 text-xs text-navy-300">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-shield-user-line text-gold-400" />
              <span className="font-medium text-white capitalize">{user.role} Mode</span>
            </div>
            <span>{user.full_name}</span>
          </div>
        )}
      </div>
    </aside>
  );
}